import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  HelpCircle,
  Loader2,
  Mail,
  MessageSquare,
  Plus,
  Search,
  Send,
  Star,
} from 'lucide-react';
import apiClient from '@/api/client';
import { ENDPOINTS } from '@/api/endpoints';

type TFn = (key: string, options?: unknown) => string;

interface TicketMessage {
  _id?: string;
  senderRole?: string;
  content: string;
  sentAt?: string;
}

interface Ticket {
  _id: string;
  ticketNumber?: string;
  subject: string;
  category?: string;
  status: string;
  messages?: TicketMessage[];
  lastActivityAt?: string;
  createdAt?: string;
  userRating?: { rating?: number };
}

const CATEGORIES = ['Technical', 'Billing', 'Account', 'Feature Request', 'General', 'Other'];

export default function SupportCenter({ theme, t }: { theme: string; t: TFn }) {
  const isDark = theme === 'dark';

  const card = `rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;
  const heading = isDark ? 'text-white' : 'text-gray-900';
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark
      ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const primaryBtn =
    'inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const ghostBtn = `inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
    isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-700/50' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
  }`;

  const k = (key: string, defaultValue: string) =>
    t(`enduser.profile.supporttab.${key}`, { defaultValue });

  const [view, setView] = useState<'main' | 'new' | 'detail'>('main');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [listError, setListError] = useState('');

  const [faqQuery, setFaqQuery] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const [form, setForm] = useState({ category: 'General', subject: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [stars, setStars] = useState(0);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  const faqs = useMemo(
    () =>
      [
        { q: k('faq.q1', 'How do I connect to a KonectX hotspot?'), a: k('faq.a1', 'Open the Hotspots page, pick a nearby hotspot and tap Connect. Then join the KonectX.net network and the login portal will open automatically to sign you in.') },
        { q: k('faq.q2', 'The WiFi login page (captive portal) does not appear. What can I do?'), a: k('faq.a2', 'Turn WiFi off and on again, or forget the network and reconnect. You can also open your browser and visit any website to trigger the login page.') },
        { q: k('faq.q3', 'How is my data usage tracked?'), a: k('faq.a3', 'Every session is linked to your account. Usage figures can take a little while to appear on your dashboard.') },
        { q: k('faq.q4', 'How do I change or cancel my subscription?'), a: k('faq.a4', 'Go to the Subscriptions page to switch plans, turn auto-renew on or off, or cancel your plan.') },
        { q: k('faq.q5', 'How do I manage the devices linked to my account?'), a: k('faq.a5', 'In Profile, open the Devices tab to see every linked device and revoke any you no longer use.') },
        { q: k('faq.q6', 'How fast does support reply?'), a: k('faq.a6', 'Usually within 24 hours. Replies appear right here in your tickets, and you will also be notified.') },
      ].map((f, i) => ({ ...f, i })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const filteredFaqs = useMemo(() => {
    const q = faqQuery.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
  }, [faqs, faqQuery]);

  const loadTickets = async () => {
    setLoadingTickets(true);
    setListError('');
    try {
      const res = await apiClient.get(ENDPOINTS.SUPPORT.TICKETS);
      setTickets(res.data?.data || []);
    } catch {
      setListError(k('errorLoad', 'Could not load. Please try again.'));
    } finally {
      setLoadingTickets(false);
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openTicket = async (id: string) => {
    setView('detail');
    setLoadingTicket(true);
    setActionError('');
    setStars(0);
    setReply('');
    try {
      const res = await apiClient.get(ENDPOINTS.SUPPORT.TICKET_DETAILS(id));
      setTicket(res.data?.data || null);
    } catch {
      setTicket(null);
      setActionError(k('errorLoad', 'Could not load. Please try again.'));
    } finally {
      setLoadingTicket(false);
    }
  };

  const submitTicket = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      setFormError(k('form.required', 'Please fill in both the subject and the description.'));
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const res = await apiClient.post(ENDPOINTS.SUPPORT.TICKETS, {
        subject: form.subject.trim(),
        category: form.category,
        description: form.description.trim(),
      });
      const created = res.data?.data;
      setForm({ category: 'General', subject: '', description: '' });
      loadTickets();
      if (created && created._id) {
        setTicket(created);
        setView('detail');
      } else {
        setView('main');
      }
    } catch {
      setFormError(k('errorSubmit', 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!ticket || !reply.trim()) return;
    setSending(true);
    setActionError('');
    try {
      const res = await apiClient.post(ENDPOINTS.SUPPORT.REPLY(ticket._id), { content: reply.trim() });
      setTicket(res.data?.data || ticket);
      setReply('');
      loadTickets();
    } catch {
      setActionError(k('errorSubmit', 'Something went wrong. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const closeCurrentTicket = async () => {
    if (!ticket) return;
    setClosing(true);
    setActionError('');
    try {
      const res = await apiClient.patch(ENDPOINTS.SUPPORT.CLOSE(ticket._id));
      setTicket(res.data?.data || ticket);
      loadTickets();
    } catch {
      setActionError(k('errorSubmit', 'Something went wrong. Please try again.'));
    } finally {
      setClosing(false);
    }
  };

  const submitRating = async () => {
    if (!ticket || stars < 1) return;
    setRatingBusy(true);
    setActionError('');
    try {
      const res = await apiClient.post(ENDPOINTS.SUPPORT.RATE(ticket._id), { rating: stars });
      setTicket(res.data?.data || ticket);
    } catch {
      setActionError(k('errorSubmit', 'Something went wrong. Please try again.'));
    } finally {
      setRatingBusy(false);
    }
  };

  const statusClasses: Record<string, string> = {
    open: 'bg-blue-500/15 text-blue-500 border-blue-500/30',
    pending: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
    in_progress: 'bg-violet-500/15 text-violet-500 border-violet-500/30',
    waiting_for_user: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
    resolved: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
    closed: 'bg-gray-500/15 text-gray-500 border-gray-500/30',
  };

  const statusLabel = (s: string) =>
    k(`status.${s}`, s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));

  const StatusBadge = ({ status }: { status: string }) => (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${statusClasses[status] || statusClasses.open}`}>
      {statusLabel(status)}
    </span>
  );

  const categoryKeyMap: Record<string, string> = {
    Technical: 'technical',
    Billing: 'billing',
    Account: 'account',
    'Feature Request': 'feature',
    General: 'general',
    Other: 'other',
  };
  const categoryLabel = (c?: string) => (c ? k(`form.categories.${categoryKeyMap[c] || 'other'}`, c) : '');

  const fmtDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const fmtDateTime = (d?: string) =>
    d ? new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  if (view === 'new') {
    return (
      <div className="space-y-6">
        <button type="button" onClick={() => setView('main')} className={`inline-flex items-center gap-1.5 text-sm font-medium ${sub} hover:text-blue-500 transition-colors`}>
          <ChevronLeft className="w-4 h-4" />
          {k('detail.back', 'Back')}
        </button>
        <div className={`${card} p-5`}>
          <h3 className={`font-semibold mb-4 ${heading}`}>{k('form.title', 'Open a new ticket')}</h3>
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${heading}`}>{k('form.category', 'Category')}</label>
              <select
                value={form.category}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setForm({ ...form, category: e.target.value })}
                className={inputCls}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {categoryLabel(c)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${heading}`}>{k('form.subject', 'Subject')}</label>
              <input
                value={form.subject}
                maxLength={200}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, subject: e.target.value })}
                placeholder={k('form.subjectPlaceholder', 'A short summary of your request')}
                className={inputCls}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${heading}`}>{k('form.description', 'Description')}</label>
              <textarea
                value={form.description}
                maxLength={5000}
                rows={5}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, description: e.target.value })}
                placeholder={k('form.descriptionPlaceholder', 'Describe your issue in as much detail as possible...')}
                className={`${inputCls} resize-none`}
              />
            </div>
            {formError && <p className="text-sm text-red-500">{formError}</p>}
            <div className="flex items-center gap-3">
              <button type="button" onClick={submitTicket} disabled={submitting} className={primaryBtn}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? k('form.submitting', 'Submitting...') : k('form.submit', 'Submit ticket')}
              </button>
              <button type="button" onClick={() => setView('main')} className={ghostBtn}>
                {k('form.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'detail') {
    const status = ticket ? ticket.status : 'open';
    const isClosed = status === 'closed';
    const canRate = !!ticket && ['resolved', 'closed'].includes(status) && !ticket.userRating?.rating;
    const visibleMessages = (ticket && ticket.messages ? ticket.messages : []).filter((m) => m && m.content);
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => {
            setView('main');
            setTicket(null);
          }}
          className={`inline-flex items-center gap-1.5 text-sm font-medium ${sub} hover:text-blue-500 transition-colors`}
        >
          <ChevronLeft className="w-4 h-4" />
          {k('detail.back', 'Back')}
        </button>

        {loadingTicket ? (
          <div className={`${card} p-8 flex justify-center`}>
            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          </div>
        ) : !ticket ? (
          <div className={`${card} p-8`}>
            <p className="text-sm text-red-500 text-center">{actionError || k('errorLoad', 'Could not load. Please try again.')}</p>
          </div>
        ) : (
          <>
            <div className={`${card} p-5`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={`text-xs ${sub}`}>{ticket.ticketNumber}</p>
                  <h3 className={`font-semibold mt-0.5 ${heading}`}>{ticket.subject}</h3>
                  <p className={`text-xs mt-1 ${sub}`}>
                    {categoryLabel(ticket.category)} · {fmtDate(ticket.createdAt)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={status} />
                  {!isClosed && (
                    <button type="button" onClick={closeCurrentTicket} disabled={closing} className={ghostBtn}>
                      {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {k('detail.close', 'Close ticket')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className={`${card} p-5`}>
              <div className="space-y-3">
                {visibleMessages.map((m, idx) => {
                  const mine = (m.senderRole || 'user') === 'user';
                  return (
                    <div key={m._id || idx} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] rounded-xl px-4 py-2.5 ${
                          mine ? 'bg-blue-600 text-white' : isDark ? 'bg-gray-700 text-gray-100' : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <p className={`text-xs font-medium mb-0.5 ${mine ? 'text-blue-100' : sub}`}>
                          {mine ? k('detail.you', 'You') : k('detail.support', 'Support')}
                        </p>
                        <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                        <p className={`text-[11px] mt-1 ${mine ? 'text-blue-200' : sub}`}>{fmtDateTime(m.sentAt)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {isClosed ? (
                <p className={`text-sm text-center mt-5 pt-4 border-t ${sub} ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  {k('detail.closedNotice', 'This ticket is closed. If you still need help, open a new ticket.')}
                </p>
              ) : (
                <div className={`mt-5 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-end gap-2">
                    <textarea
                      value={reply}
                      maxLength={2000}
                      rows={2}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReply(e.target.value)}
                      placeholder={k('detail.replyPlaceholder', 'Write a reply...')}
                      className={`${inputCls} resize-none`}
                    />
                    <button type="button" onClick={sendReply} disabled={sending || !reply.trim()} className={`${primaryBtn} shrink-0`}>
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              {actionError && <p className="text-sm text-red-500 mt-3">{actionError}</p>}
            </div>

            {canRate && (
              <div className={`${card} p-5`}>
                <h3 className={`font-semibold mb-3 ${heading}`}>{k('detail.rateTitle', 'How was our support?')}</h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setStars(n)} className="p-0.5">
                        <Star className={`w-6 h-6 ${n <= stars ? 'text-amber-400 fill-amber-400' : sub}`} />
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={submitRating} disabled={ratingBusy || stars < 1} className={primaryBtn}>
                    {ratingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {k('detail.rateSubmit', 'Submit rating')}
                  </button>
                </div>
              </div>
            )}
            {ticket.userRating?.rating ? (
              <p className={`text-sm text-center ${sub}`}>{k('detail.rated', 'Thanks for your feedback!')}</p>
            ) : null}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={`${card} p-5 flex flex-col sm:flex-row sm:items-center gap-4`}>
        <div className={`p-3 rounded-lg self-start ${isDark ? 'bg-blue-500/15' : 'bg-blue-50'}`}>
          <Mail className="w-5 h-5 text-blue-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold ${heading}`}>{k('contactTitle', 'Contact support')}</h3>
          <p className={`text-sm mt-0.5 ${sub}`}>{k('contactBody', 'Our team usually replies within 24 hours.')}</p>
        </div>
        <a href="mailto:support@konectx.net" className={primaryBtn}>
          <Mail className="w-4 h-4" />
          {k('emailButton', 'Email us')}
        </a>
      </div>

      <div className={`${card} p-5`}>
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-blue-500" />
          <h3 className={`font-semibold ${heading}`}>{k('faqTitle', 'Frequently asked questions')}</h3>
        </div>
        <div className="relative mb-4">
          <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${sub}`} />
          <input
            value={faqQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFaqQuery(e.target.value)}
            placeholder={k('faqSearch', 'Search the FAQ...')}
            className={`${inputCls} pl-9`}
          />
        </div>
        {filteredFaqs.length === 0 ? (
          <p className={`text-sm py-4 text-center ${sub}`}>{k('noFaqResults', 'No results. Try different keywords or open a ticket below.')}</p>
        ) : (
          <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {filteredFaqs.map((f) => (
              <div key={f.i}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === f.i ? null : f.i)}
                  className="w-full flex items-center justify-between gap-3 py-3 text-left"
                >
                  <span className={`text-sm font-medium ${heading}`}>{f.q}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${sub} ${openFaq === f.i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === f.i && <p className={`text-sm pb-3 leading-relaxed ${sub}`}>{f.a}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`${card} p-5`}>
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-500" />
            <h3 className={`font-semibold ${heading}`}>{k('myTickets', 'My tickets')}</h3>
          </div>
          <button
            type="button"
            onClick={() => {
              setFormError('');
              setView('new');
            }}
            className={primaryBtn}
          >
            <Plus className="w-4 h-4" />
            {k('newTicket', 'New ticket')}
          </button>
        </div>
        {loadingTickets ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          </div>
        ) : listError ? (
          <p className="text-sm text-red-500 py-4 text-center">{listError}</p>
        ) : tickets.length === 0 ? (
          <div className="text-center py-8">
            <p className={`text-sm font-medium ${heading}`}>{k('noTickets', 'No tickets yet')}</p>
            <p className={`text-sm mt-1 ${sub}`}>{k('noTicketsBody', 'Open a ticket and our team will get back to you quickly.')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tickets.map((tk) => (
              <button
                key={tk._id}
                type="button"
                onClick={() => openTicket(tk._id)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  isDark ? 'border-gray-700 hover:bg-gray-700/40' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${heading}`}>{tk.subject}</p>
                    <p className={`text-xs mt-0.5 ${sub}`}>
                      {tk.ticketNumber} · {categoryLabel(tk.category)} · {fmtDate(tk.lastActivityAt || tk.createdAt)}
                    </p>
                  </div>
                  <StatusBadge status={tk.status} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
